import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, clearToken, getToken, setToken } from './api';

export type Me =
  | { scope: 'staff'; user: { id: string; name: string; role: string; phone: string } }
  | { scope: 'client'; client: { id: string; name: string; code: string; phone: string } }
  | null;

type Ctx = {
  me: Me;
  loading: boolean;
  login: (token: string, scope: string) => Promise<void>;
  logout: () => void;
  reload: () => Promise<void>;
};

const AuthCtx = createContext<Ctx>({
  me: null,
  loading: true,
  login: async () => undefined,
  logout: () => undefined,
  reload: async () => undefined,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!getToken()) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      setMe(await api<Me>('/auth/me'));
    } catch {
      clearToken();
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const value = useMemo<Ctx>(
    () => ({
      me,
      loading,
      login: async (token, scope) => {
        setToken(token, scope);
        setLoading(true);
        await reload();
      },
      logout: () => {
        void api('/auth/logout', { method: 'POST', body: {} }).catch(() => undefined);
        clearToken();
        setMe(null);
      },
      reload,
    }),
    [me, loading, reload],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
